import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getTemperatureSizeSales } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import {
  SizeModel,
  SizeResponseModel,
  StyleModel,
  StyleResponseModel,
  TemperatureSizeSalesModel,
} from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

export default function useTemperatureSizeSales(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [salesByStyleChartData, setSalesByStyleChartData] =
    useState<ChartDataModel>()
  const [salesBySizeChartData, setSalesBySizeChartData] =
    useState<ChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchTemperatureSizeSalesData(
        branches,
        startDate,
        endDate,
        itemGroupName
      )

      const salesByStyleChartData: ChartDataModel = data?.salesByStyle
        ? {
            data: Object.fromEntries(
              data.salesByStyle.map((style) => [
                style.style,
                style.netSalesAmount,
              ])
            ),
            labels: data.salesByStyle.map((style) => style.style),
          }
        : { data: {}, labels: [] }

      const salesBySizeChartData: ChartDataModel = data?.salesBySize
        ? {
            data: Object.fromEntries(
              data.salesBySize.map((size) => [size.size, size.netSalesAmount])
            ),
            labels: data.salesBySize.map((size) => size.size),
          }
        : { data: {}, labels: [] }

      setSalesByStyleChartData(salesByStyleChartData)
      setSalesBySizeChartData(salesBySizeChartData)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemGroupName])

  const refetch = useCallback(() => {
    console.log('Refetching temperature size sales...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    salesByStyleChartData,
    salesBySizeChartData,
    loading,
    refetch,
  }
}

async function fetchTemperatureSizeSalesData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string
): Promise<{
  data: TemperatureSizeSalesModel
}> {
  try {
    const response = await getTemperatureSizeSales(
      branches,
      startDate,
      endDate,
      itemGroupName
    )

    const data: TemperatureSizeSalesModel = convertData(
      response.sales_by_size,
      response.sales_by_style
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  salesBySize: SizeResponseModel[],
  salesByStyle: StyleResponseModel[]
): TemperatureSizeSalesModel {
  const salesBySizeData: SizeModel[] = salesBySize.map((size) => {
    return {
      size: size.size,
      netSalesAmount: formatMetric(size.net_sales_amount, 2),
    }
  })

  const salesByStyleData: StyleModel[] = salesByStyle.map((style) => {
    return {
      style: style.style,
      netSalesAmount: formatMetric(style.net_sales_amount, 2),
    }
  })
  return {
    salesBySize: salesBySizeData,
    salesByStyle: salesByStyleData,
  }
}
